"use client";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { Config, Template, Styling } from "src/types";
import { useFetchCompanyInfo } from "src/app/lib/fetchCompanyInfo";
import { generateTypographyClasses } from "src/app/lib/useCssGenerator";
import { mockConfig } from "../constants/mockConfig";
import { fetchConfig } from "src/app/lib/fetchConfig";
import { getClientDomain } from "src/app/lib/useFetchUnits";

export const fontVariableMap: Record<string, string> = {
  Roboto: "var(--font-roboto)",
  "Open Sans": "var(--font-open-sans)",
  Lora: "var(--font-lora)",
  Montserrat: "var(--font-montserrat)",
  Poppins: "var(--font-poppins)",
  Raleway: "var(--font-raleway)",
  Inter: "var(--font-inter)",
  "Playfair Display": "var(--font-playfair)",
  Lato: "var(--font-lato)",
  // System fonts fallback
  Arial: "Arial, sans-serif",
  "Courier New": "'Courier New', Courier, monospace",
  "Times New Roman": "'Times New Roman', Times, serif",
  Georgia: "Georgia, serif",
  Verdana: "Verdana, Geneva, sans-serif",
};

// Utility to convert hex to RGB
export function hexToRgb(hex: string): string {
  const cleanedHex = hex.replace("#", "");
  const r = parseInt(cleanedHex.substring(0, 2), 16);
  const g = parseInt(cleanedHex.substring(2, 4), 16);
  const b = parseInt(cleanedHex.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}
// Define the ThemeContext type
interface ThemeContextType {
  config: Config | null | undefined;
  setStyling: (styling: Styling) => void;
  setTemplate: (template: Template) => void;
  getConfig: () => Config | null | undefined;
}
// Create the ThemeContext with default values
const ThemeContext = createContext<ThemeContextType>({
  config: null,
  setStyling: () => { },
  setTemplate: () => { },
  getConfig: () => null,
});

// ThemeProvider component
export const ThemeProvider = ({ 
  children, 
  initialConfig 
}: { 
  children: ReactNode;
  initialConfig?: Config;
}) => {
  const [config, setConfig] = useState<Config | undefined>(initialConfig);

  useEffect(() => {
    async function loadConfig() {
      const isDemoMode = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
      
      // In Real Mode, if we have initialConfig from server, we are done.
      if (!isDemoMode && config) return;

      const location = getClientDomain("theme provider");

      if (!isDemoMode) {
        const fetchedConfig = await fetchConfig(location);
        setConfig(fetchedConfig);
        return;
      }

      // In Demo Mode, check localStorage for any user-selected template overrides
      if (typeof window !== "undefined") {
        const stored = localStorage.getItem("themeConfig");
        if (stored) {
          try {
            const parsed = JSON.parse(stored) as Config;
            // Only update if it's different from the initial mockConfig
            if (JSON.stringify(parsed) !== JSON.stringify(config)) {
              setConfig(parsed);
            }
            return;
          } catch (e) {
            console.error("Failed to parse themeConfig from localStorage", e);
          }
        }
      }

      // If no localStorage, ensure we at least have mockConfig
      if (!config) {
        setConfig(mockConfig);
      }
    }
    loadConfig();
  }, []);

  useEffect(() => {
    if (config?.styling) {
      applyStylingToCSS(config.styling);
    }
  }, [config?.styling]);

  // Fetch company info using the custom hook
  const { data } = useFetchCompanyInfo();
  useEffect(() => {
    if (data?.websiteSettings?.typography) {
      generateTypographyClasses(data.websiteSettings.typography);
    }
  }, [data]);

  // Initialize config from fetched data and apply styling
  // useEffect(() => {
  //   // Don't override if config was already loaded from localStorage
  //   if (!config && data) {
  //     const updatedConfig = {
  //       ...data,
  //       styling: {
  //         ...data.styling,
  //       },
  //     };
  //     setConfig(updatedConfig);
  //     if (updatedConfig.styling) {
  //       applyStylingToCSS(updatedConfig.styling);
  //     }

  //     const mainElement = document.getElementById("main");
  //     if (mainElement) {
  //       if (
  //         updatedConfig.template === "design2" ||
  //         updatedConfig.template === "design3"
  //       ) {
  //         mainElement.style.paddingTop = "60px";
  //       } else {
  //         mainElement.style.paddingTop = "0px";
  //       }
  //     }
  //   }
  // }, [data, config]);

  // Persist config to localStorage whenever it changes
  useEffect(() => {
    if (config) {
      localStorage.setItem("themeConfig", JSON.stringify(config));
    }
  }, [config]);

  // Apply styling to CSS custom properties

  const applyStylingToCSS = (styling: Styling) => {
    const primaryColorRgb = hexToRgb(styling.primaryColor);
    const root = document.documentElement;
    const toRem = (px: string) => `${parseInt(px) / 16}rem`;
    // Set color and size properties
    root.style.setProperty("--primary-color", styling.primaryColor);
    root.style.setProperty("--primary-color-rgb", primaryColorRgb);
    root.style.setProperty(
      "--h1-size",
      `clamp(2rem, 5.5vw, ${toRem(styling.h1Size)})`
    );
    // Function to format the font for Google Fonts URL
    const formatFontForGoogle = (font: string): string => {
      const cleanFont = font.split(",")[0].trim(); // Remove fallback font
      return cleanFont.replace(/\s+/g, "+"); // Replace spaces with '+'
    };
    // Dynamically generate the Google Fonts URL based on the backend font name
    const googleFontUrl = `https://fonts.googleapis.com/css2?family=${formatFontForGoogle(
      styling.fontFamily
    )}&display=swap`;

    // Check if the link is not already added
    if (!document.querySelector(`link[href="${googleFontUrl}"]`)) {
      const linkTag = document.createElement("link");
      linkTag.rel = "stylesheet";
      linkTag.href = googleFontUrl;
      document.head.appendChild(linkTag);
    }
    // Apply the font family directly using the backend font name.
    root.style.setProperty(
      "--font-family",
      `'${styling.fontFamily}', sans-serif`
    );
  };
  // Determine the font class for Google Fonts based on the current fontFamily
  // Setter for styling
  const setStyling = (styling: Styling) => {
    if (!config) return;
    const updated = { ...config, styling };
    setConfig(updated);
    applyStylingToCSS(styling);
  };

  // Setter for template
  const setTemplate = (template: Template) => {
    if (!config) return;

    // Update config state with the new template
    const updated = { ...config, template };
    setConfig(updated);
    // Get the #main element
    const mainElement = document.getElementById("main");
    // Apply styles based on the template
    if (mainElement) {
      if (template === "design2" || template === "design3") {
        mainElement.style.paddingTop = "60px"; // Apply padding for design2 and design3
      } else {
        mainElement.style.paddingTop = "0px"; // Apply default padding for design1
      }
    }
  };

  // Getter for config
  const getConfig = () => config;

  return (
    <ThemeContext.Provider
      value={{ config, setStyling, setTemplate, getConfig }}
    >
      <div>{children}</div>
    </ThemeContext.Provider>
  );
};

// Custom hook to access the ThemeContext
export const useTheme = () => useContext(ThemeContext);
